import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getOnboardingConfig } from "../api/sellerOnboarding";

/**
 * Is Seller onboarding switched on for this account? Admin-only lookup used
 * by the main nav ("Seller applications"). Module cache so the nav and other
 * mounts share one request. Defaults to OFF while unknown or on error, and
 * never calls the API for non-admins.
 */
let cache = {
  enterpriseId: "",
  enabled: false,
  loaded: false,
};
let inflight = null;

const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn(cache);
    } catch {
      /* ignore */
    }
  });
};

/** Called by Settings → Seller onboarding after load/save. */
export const setSellerOnboardingEnabledCache = (enabled, enterpriseId) => {
  cache = {
    enterpriseId: String(enterpriseId ?? cache.enterpriseId ?? ""),
    enabled: enabled === true,
    loaded: true,
  };
  notify();
};

/** Test hook. */
export const resetSellerOnboardingEnabledCache = () => {
  cache = { enterpriseId: "", enabled: false, loaded: false };
  inflight = null;
};

/** Current cached value for an account (false when unknown). */
export const readSellerOnboardingEnabled = (enterpriseId) =>
  cache.loaded && cache.enterpriseId === String(enterpriseId || "")
    ? cache.enabled
    : false;

/**
 * Load once per account (shared across callers). Non-admins never call the
 * API and resolve false; errors resolve false.
 */
export function loadSellerOnboardingEnabled(enterpriseId, isAdmin) {
  const eid = String(enterpriseId || "");
  if (!isAdmin || !eid) return Promise.resolve(false);
  if (cache.loaded && cache.enterpriseId === eid) return Promise.resolve(cache.enabled);
  if (inflight && inflight.enterpriseId === eid) return inflight.promise;
  const promise = getOnboardingConfig()
    .then((body) => body?.config?.enabled === true)
    .catch(() => false)
    .then((on) => {
      setSellerOnboardingEnabledCache(on, eid);
      return on;
    })
    .finally(() => {
      if (inflight?.promise === promise) inflight = null;
    });
  inflight = { enterpriseId: eid, promise };
  return promise;
}

/** Subscribe to cache changes; returns unsubscribe. */
export function subscribeSellerOnboardingEnabled(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useSellerOnboardingEnabled() {
  const enterpriseId = useSelector(
    (s) => s?.currentUser?.user?.enterpriseId || ""
  );
  const isAdmin = useSelector(
    (s) => !!s?.currentUser?.user?.isEnterpriseAdmin
  );
  const fresh = cache.loaded && cache.enterpriseId === enterpriseId;
  const [enabled, setEnabled] = useState(fresh ? cache.enabled : false);
  const [loading, setLoading] = useState(isAdmin && !fresh);

  useEffect(
    () =>
      subscribeSellerOnboardingEnabled((next) => {
        if (next.enterpriseId !== String(enterpriseId || "")) return;
        setEnabled(isAdmin && next.enabled === true);
        setLoading(!next.loaded);
      }),
    [enterpriseId, isAdmin]
  );

  useEffect(() => {
    if (!isAdmin || !enterpriseId) {
      setEnabled(false);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const known = cache.loaded && cache.enterpriseId === enterpriseId;
    if (!known) setLoading(true);
    loadSellerOnboardingEnabled(enterpriseId, isAdmin).then((on) => {
      if (cancelled) return;
      setEnabled(on);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, isAdmin]);

  return { enabled, loading };
}
